import React from "react";
import { Select, SelectItem } from "@heroui/react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const cn = (...inputs) => twMerge(clsx(inputs));

const CustomSelect = ({
  options,
  selectedKeys,
  onSelectionChange,
  placeholder = "Select an option",
  label,
  width = "w-full",
  showDescription = true,
  endContent = null,
  classNames = {},
  containerClassName = "",
  labelClassName = "",
  triggerClassName = "",
  valueClassName = "",
  popoverClassName = "",
  popoverContentClassName = "",
  /** When false, dropdown panel sizes to option text (trigger width unchanged). */
  popoverMatchTriggerWidth = true,
  popoverProps: userPopoverProps = {},
  selectItemClassName = "",
  selectItemTitleClassName = "",
  selectItemDescriptionClassName = "",
  disabled = false,
  error = false,
  errorMessage = "",
  errorClassName = "",
  size = "md",
  variant = "flat",
  color = "default",
}) => {
  const labelId = label
    ? `select-label-${label.replace(/\s+/g, "-").toLowerCase()}`
    : undefined;

  // Trigger: use ! on bg/border so HeroUI variant + innerWrapper don't flash back to theme defaults
  const baseTriggerClasses = cn(
    width,
    "group text-[0.815rem] font-inter rounded-lg py-2 px-3 shadow-sm",
    "border border-gray-300 dark:border-gray-700 !bg-white dark:!bg-gray-900 transition-[background-color,border-color,box-shadow]",
    "hover:!bg-gray-50 dark:hover:!bg-gray-800 data-[hover=true]:!bg-gray-50 dark:data-[hover=true]:!bg-gray-800",
    "data-[open=true]:!bg-white dark:data-[open=true]:!bg-gray-900 data-[open=true]:!border-secondary",
    "data-[focus=true]:!bg-white dark:data-[focus=true]:!bg-gray-900 data-[focus=true]:!border-secondary",
    "hover:!border-secondary/70 data-[hover=true]:!border-secondary/70",
    "focus-within:!border-secondary",
    "outline-none ring-0 ring-offset-0",
    "focus-within:ring-2 focus-within:ring-secondary/25",
    "data-[focus=true]:ring-2 data-[focus=true]:ring-secondary/25",
    "[&[data-placeholder=true]]:text-gray-500 dark:[&[data-placeholder=true]]:text-gray-400",
    error &&
      "!border-red-500 focus-within:!border-red-500 data-[focus=true]:!border-red-500 focus-within:!ring-red-200",
    disabled && "opacity-50 cursor-not-allowed",
    triggerClassName,
  );

  const baseInnerWrapperClasses = cn(
    "!bg-transparent min-w-0 max-w-full shadow-none",
    classNames?.innerWrapper,
  );

  const baseMainWrapperClasses = cn(
    "!bg-transparent shadow-none",
    classNames?.mainWrapper,
  );

  // Base value styles
  const baseValueClasses = cn(
    "text-gray-500 dark:text-gray-400 data-[has-value=true]:text-gray-800 dark:data-[has-value=true]:text-gray-100 font-inter text-[0.815rem] data-[placeholder=true]:text-gray-500 dark:data-[placeholder=true]:text-gray-400",
    valueClassName,
  );

  // Base label styles
  const baseLabelClasses = cn(
    "text-[0.815rem] text-gray-800 dark:text-gray-100 font-inter font-medium",
    labelClassName,
  );

  // Base select item styles
  const baseSelectItemClasses = cn(
    "hover:!bg-[#207C9733] data-[hover=true]:!bg-[#207C9733] hover:!text-secondary data-[hover=true]:!text-secondary text-gray-800 dark:text-gray-100",
    selectItemClassName,
  );

  // Base select item title styles
  const baseSelectItemTitleClasses = cn(
    "font-normal text-gray-800 dark:text-gray-100",
    selectItemTitleClassName,
  );

  // Base select item description styles
  const baseSelectItemDescriptionClasses = cn(
    "text-xs text-gray-500",
    selectItemDescriptionClassName,
  );

  const popoverPanelWidthClasses = popoverMatchTriggerWidth
    ? ""
    : "!w-auto !min-w-max";

  const popoverContentClasses = cn(
    "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg z-[100000001]",
    popoverPanelWidthClasses,
    popoverContentClassName,
  );

  const mergedPopoverClassNames =
    typeof popoverClassName === "object" && popoverClassName !== null
      ? popoverClassName
      : { content: popoverClassName };

  // Base container styles
  const baseContainerClasses = cn("flex flex-col gap-1.5", containerClassName);

  const {
    trigger: userTrigger,
    value: userValue,
    popoverContent: userPopoverContent,
    base: userBase,
    innerWrapper: _iw,
    mainWrapper: _mw,
    ...restClassNames
  } = classNames || {};

  const mergedClassNames = {
    base: cn(
      "w-full max-w-full border-none !bg-transparent p-0 shadow-none rounded-lg",
      userBase,
    ),
    trigger: cn(baseTriggerClasses, userTrigger),
    value: cn(baseValueClasses, userValue),
    popoverContent: cn(popoverContentClasses, userPopoverContent),
    innerWrapper: baseInnerWrapperClasses,
    mainWrapper: baseMainWrapperClasses,
    ...restClassNames,
    listbox: cn(
      popoverMatchTriggerWidth ? "" : "!w-auto !min-w-max",
      restClassNames.listbox,
    ),
    listboxWrapper: cn(
      showDescription && "max-h-[min(280px,40vh)] overflow-y-auto",
      restClassNames.listboxWrapper,
    ),
  };

  return (
    <div className={baseContainerClasses}>
      {label && (
        <label id={labelId} className={baseLabelClasses}>
          {label}
        </label>
      )}
      <Select
        aria-label={label || placeholder}
        aria-labelledby={labelId}
        classNames={mergedClassNames}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        placeholder={placeholder}
        endContent={endContent}
        disabled={disabled}
        size={size}
        variant={variant}
        color={color}
        popoverProps={{
          style: { zIndex: 100000001 },
          offset: 4,
          classNames: {
            ...mergedPopoverClassNames,
            content: cn(popoverContentClasses, mergedPopoverClassNames.content),
          },
          ...userPopoverProps,
        }}
      >
        {options.map((option) => {
          const hasDescription = showDescription && !!option.description;
          return (
            <SelectItem
              key={option.key}
              description={hasDescription ? option.description : undefined}
              textValue={
                option.searchText ??
                [option.title, option.description].filter(Boolean).join(" ")
              }
              isDisabled={option.disabled}
              classNames={{
                base: cn(
                  baseSelectItemClasses,
                  hasDescription &&
                    "!h-auto !min-h-0 py-2 items-start whitespace-normal",
                ),
                title: cn(
                  baseSelectItemTitleClasses,
                  hasDescription && "!text-xs !font-medium",
                ),
                description: cn(
                  baseSelectItemDescriptionClasses,
                  hasDescription && "!text-[10px] whitespace-normal line-clamp-2",
                ),
              }}
            >
              {option.title}
            </SelectItem>
          );
        })}
      </Select>
      {error && errorMessage && (
        <span
          className={cn("text-xs text-red-500 font-medium", errorClassName)}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default CustomSelect;
